import { useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { MdAttachFile, MdSend } from 'react-icons/md';
import { post } from 'api/api';
import { camelizeKeys } from 'humps';
import { ButtonNew, ButtonReset, Buttons, LoadingButton, MAIN_SIDEBAR_WIDTH, media, SECONDARY_SIDEBAR_WIDTH } from 'ui';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import AttachmentNew, { MessageAttachments } from 'components/AttachmentNew';
import { ModalBody, ModalContent, ModalHeader, ModalTitle } from 'ui/modal';
import Modal from 'components/Modal';
import TextField from 'components/TextField';
import { SERGIC_OFFER } from '../constants';
import { useTranslation, withTranslation } from 'react-i18next';
import { useBreakpoint } from '../hooks';

const AttachmentLabel = styled.label`
   display: flex;
   cursor: pointer;
   color: ${({ theme }) => theme.text};
   position: absolute;
   left: ${({ theme }) => theme.spacing()};
   top: 50%;
   transform: translateY(-50%);
   align-items: center;
   font-weight: 600;
   color: ${({ theme }) => theme.primary};

   > span {
      display: none;
   }

   > svg {
      font-size: ${({ theme }) => theme.size(3)};
   }

   ${media.tablet`
    top: ${({ theme }) => theme.spacing(0.75)};
    right: ${({ theme }) => theme.spacing()};
    left: auto;
    transform: none;
    position: static;

    > span {
      display: block;
    }
  `};
`;

const FormInner = styled.div`
   border: 1px solid ${({ theme }) => theme.separator};
   display: flex;
   flex-wrap: wrap;
   border-radius: ${({ theme }) => theme.borderRadius};
   margin-left: ${({ theme }) => theme.spacing(3)};
   margin-right: ${({ theme }) => theme.spacing(3)};

   ${media.tablet`
    margin-left: 0;
    margin-right: 0;
  `};
`;

const Actions = styled.div`
   display: flex;
   align-items: center;
   justify-content: space-between;

   ${media.tablet`
    margin-top: ${({ theme }) => theme.spacing(0.5)};
  `};
`;

const Wrapper = styled.form`
   position: fixed;
   bottom: 55px;
   right: 0;
   padding: ${({ theme }) => theme.spacing(0.5)} 0;
   background-color: #fff;
   border-top: 1px solid ${({ theme }) => theme.separator};
   left: 0;

   @supports (padding: max(0px)) {
      bottom: max(55px, calc(env(safe-area-inset-bottom) + 55px));
   }

   ${media.tablet`
    width: calc(100% - 300px - ${MAIN_SIDEBAR_WIDTH});
    bottom: 0;
    left: auto;
    padding-top: ${({ theme }) => theme.spacing()};
    padding-left: ${({ theme }) => theme.spacing()};
    padding-right: ${({ theme }) => theme.spacing()};
    padding-bottom: ${({ theme }) => theme.spacing(3)};
  `};

   ${media.desktop`
    width: calc(100% - ${SECONDARY_SIDEBAR_WIDTH} - ${MAIN_SIDEBAR_WIDTH});
  `};

   ${() =>
      SERGIC_OFFER === 'DIR' &&
      css`
         padding-bottom: 60px;

         ${media.tablet`
        padding-bottom: 68px;
      `};
      `};
`;

const SendMessageButton = styled(ButtonReset)`
   color: ${({ theme }) => theme.primary};
   font-weight: 500;
   position: absolute;
   right: ${({ theme }) => theme.spacing()};
   top: 50%;
   transform: translateY(-50%);

   &:disabled {
      color: ${({ theme }) => theme.textLight};
      cursor: not-allowed;
   }
`;

const MessageForm = ({
   attachments,
   onAddAttachment,
   onRemoveAttachment,
   isDisabled,
   onChange,
   value,
   onSubmit,
   isSubmitting,
}) => {
   const { t } = useTranslation();
   const { isMobile } = useBreakpoint();
   const [newAttachment, setNewAttachment] = useState(null);
   const [newAttachmentName, setNewAttachmentName] = useState(null);
   const editorRef = useRef();

   const handleAddAttachment = e => {
      e.preventDefault();

      const reader = new FileReader();
      const file = e.target.files[0];
      reader.readAsDataURL(file);

      reader.onloadend = () => {
         setNewAttachment({
            fileUrl: reader.result,
            sourceContentType: file.type,
            sourceFileSize: file.size,
         });
         setNewAttachmentName(file.name);
      };
   };

   const handleSendAttachment = e => {
      e.preventDefault();

      post('/v1/attachments', {
         attachment: {
            image_base: newAttachment.fileUrl,
            source_file_name: newAttachmentName,
         },
      }).then(
         result => {
            setNewAttachment(null);
            setNewAttachmentName(null);
            onAddAttachment(camelizeKeys(result));
         },
         err => console.error(err)
      );
   };

   return (
      <Wrapper onSubmit={onSubmit}>
         <Modal
            isOpen={Boolean(newAttachment)}
            onRequestClose={() => {
               setNewAttachment(null);
               setNewAttachmentName('');
            }}
         >
            <ModalContent>
               <ModalHeader>
                  <ModalTitle small>{t('attachment:new')}</ModalTitle>
               </ModalHeader>

               <ModalBody>
                  <div style={{ marginLeft: -16, marginBottom: 16 }}>
                     <AttachmentNew {...newAttachment} canBeRemoved={false} shouldHavePreview={false} />
                  </div>

                  <TextField
                     label={t('attachment:fileName')}
                     value={newAttachmentName}
                     onChange={e => setNewAttachmentName(e.target.value)}
                  />
                  <Buttons>
                     <ButtonNew type='button' onClick={handleSendAttachment}>
                        {t('attachment:send')}
                     </ButtonNew>
                  </Buttons>
               </ModalBody>
            </ModalContent>
         </Modal>

         <div style={{ position: 'relative' }}>
            <FormInner>
               <ReactQuill
                  value={value}
                  onChange={onChange}
                  placeholder={t('communications:placeholder')}
                  ref={editorRef}
                  modules={{
                     toolbar: [
                        [{ header: [1, 2, false] }],
                        ['bold', 'italic', 'strike', 'underline'],
                        ['blockquote'],
                        [{ list: 'ordered' }, { list: 'bullet' }],
                     ],
                  }}
               />

               {attachments.length > 0 && (
                  <MessageAttachments isInInput shouldNotWrap>
                     {attachments.map(({ id, ...picture }) => (
                        <AttachmentNew
                           key={id}
                           id={id}
                           {...picture}
                           isInInput
                           canBeRemoved
                           onRemove={() => onRemoveAttachment(id)}
                        />
                     ))}
                  </MessageAttachments>
               )}
            </FormInner>

            <Actions>
               <AttachmentLabel htmlFor='attachment'>
                  <MdAttachFile />
                  <span>{t('attachment:add')}</span>
                  <input
                     id='attachment'
                     type='file'
                     onChange={handleAddAttachment}
                     accept='image/*,application/pdf'
                     style={{ display: 'none' }}
                  />
               </AttachmentLabel>

               {isMobile ? (
                  <SendMessageButton disabled={(attachments.length === 0 && !value) || isDisabled || isSubmitting}>
                     <span>
                        <MdSend />
                     </span>
                  </SendMessageButton>
               ) : (
                  <LoadingButton
                     isPrimary
                     disabled={(attachments.length === 0 && !value) || isDisabled || isSubmitting}
                     isLoading={isSubmitting}
                  >
                     {t('misc:send')}
                  </LoadingButton>
               )}
            </Actions>
         </div>
      </Wrapper>
   );
};

export default withTranslation()(MessageForm);
